// Design System Divider Component
// Content separator with various styles

import React from 'react';
import { Divider as MuiDivider, styled, alpha } from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens } from '../../tokens';

// Styled Divider with design system tokens
const StyledDivider = styled(MuiDivider)(({ theme, variant = 'fullWidth' }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    borderColor: alpha(colors.divider, 0.12),
    
    ...(variant === 'middle' && {
      marginLeft: spacingTokens.layout.md,
      marginRight: spacingTokens.layout.md,
    }),
    
    ...(variant === 'inset' && {
      marginLeft: spacingTokens.layout.xl,
    }),
    
    '&.MuiDivider-withChildren': {
      '&::before, &::after': {
        borderColor: alpha(colors.divider, 0.12),
      },
    },
    
    '& .MuiDivider-wrapper': {
      paddingLeft: spacingTokens.component.md,
      paddingRight: spacingTokens.component.md,
    },
  };
});

/**
 * Divider - Content separator component
 * 
 * @example
 * // Basic divider
 * <Divider />
 * 
 * // With text
 * <Divider>OR</Divider>
 * 
 * // Vertical divider
 * <Divider orientation="vertical" flexItem />
 * 
 * // Different variants
 * <Divider variant="middle" />
 */
const Divider = ({
  children,
  variant = 'fullWidth',
  orientation = 'horizontal',
  textAlign = 'center',
  flexItem = false,
  light = false,
  sx,
  ...props
}) => {
  return (
    <StyledDivider
      variant={variant}
      orientation={orientation}
      textAlign={textAlign}
      flexItem={flexItem}
      light={light}
      sx={sx}
      {...props}
    >
      {children}
    </StyledDivider>
  );
};

Divider.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(['fullWidth', 'inset', 'middle']),
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  textAlign: PropTypes.oneOf(['center', 'left', 'right']),
  flexItem: PropTypes.bool,
  light: PropTypes.bool,
  sx: PropTypes.object,
};

export default Divider;